import { useEffect, useState, useMemo } from "react";
import {
    Mail, Plus, Edit3, Copy, Trash2, Eye, CheckCircle2, Loader2,
    X, Save, AlertTriangle, Circle, BadgeCheck, RefreshCw, Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Admin → Tutor Application Email Templates
 *
 * Scoped exclusively to the "Become a Tutor" auto-confirmation email. Does NOT
 * affect student-enquiry, demo-class, or any other form's email pipeline.
 *
 * Features (per spec):
 *   ✓ Create / Edit / Duplicate / Delete / Preview templates
 *   ✓ Radio-style single-click "Set as Active" (mutually exclusive)
 *   ✓ "Currently Active" badge on the live template
 *   ✓ "Save & Activate" button in the editor
 *   ✓ Instant switching (no code changes, no redeploy)
 *   ✓ Placeholders: {{first_name}} {{name}} {{email}} {{phone}} {{city}}
 *                   {{qualification}} {{experience}} {{subjects}} {{mode}}
 */
export default function TutorEmailTemplatesPanel() {
    const [items, setItems] = useState([]);
    const [placeholders, setPlaceholders] = useState([]);
    const [sampleFields, setSampleFields] = useState({});
    const [loading, setLoading] = useState(true);
    const [flash, setFlash] = useState("");
    const [editor, setEditor] = useState(null);        // template object (existing or new draft)
    const [preview, setPreview] = useState(null);      // {subject, html}
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");

    const activeTemplate = useMemo(() => items.find((t) => t.is_active), [items]);

    const refresh = async () => {
        const { data } = await api.get("/admin/tutor-email-templates");
        setItems(data.items || []);
        setPlaceholders(data.placeholders || []);
        setSampleFields(data.sample_fields || {});
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/admin/tutor-email-templates");
                if (cancelled) return;
                setItems(data.items || []);
                setPlaceholders(data.placeholders || []);
                setSampleFields(data.sample_fields || {});
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 3500); };

    // -------- actions --------

    const onActivate = async (tpl) => {
        if (tpl.is_active) return;
        setBusy(`activate:${tpl.id}`);
        setError("");
        try {
            await api.post(`/admin/tutor-email-templates/${tpl.id}/activate`);
            await refresh();
            showFlash(`"${tpl.name}" is now the active tutor template — used for all new applications.`);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to activate template");
        } finally {
            setBusy("");
        }
    };

    const onDuplicate = async (tpl) => {
        setBusy(`dup:${tpl.id}`);
        setError("");
        try {
            await api.post(`/admin/tutor-email-templates/${tpl.id}/duplicate`);
            await refresh();
            showFlash(`Duplicated "${tpl.name}".`);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to duplicate");
        } finally {
            setBusy("");
        }
    };

    const onDelete = async (tpl) => {
        if (!window.confirm(`Delete template "${tpl.name}"?\n\nIf this template is currently active, the next-most-recent template will be auto-activated.`)) return;
        setBusy(`del:${tpl.id}`);
        setError("");
        try {
            await api.delete(`/admin/tutor-email-templates/${tpl.id}`);
            await refresh();
            showFlash(`Deleted "${tpl.name}".`);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to delete");
        } finally {
            setBusy("");
        }
    };

    const onPreview = async (tpl) => {
        setBusy(`preview:${tpl.id}`);
        setError("");
        try {
            const { data } = await api.get(`/admin/tutor-email-templates/${tpl.id}/preview`);
            setPreview({ ...data, name: tpl.name });
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to render preview");
        } finally {
            setBusy("");
        }
    };

    const onEdit = (tpl) => {
        setEditor({
            id: tpl.id,
            name: tpl.name,
            subject: tpl.subject,
            body_html: tpl.body_html,
            activate: tpl.is_active,
            mode: "edit",
        });
    };

    const onCreate = () => {
        setEditor({
            id: null,
            name: "",
            subject: "Thank you for applying — Anand Tutorials",
            body_html: "<p>Hi {{first_name}},</p>\n<p>Thank you for your interest in joining Anand Tutorials.</p>\n<p>Replace this with your message.</p>",
            activate: items.length === 0,
            mode: "create",
        });
    };

    if (loading) {
        return (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center" data-testid="tutor-templates-loading">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="tutor-email-templates-panel">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] inline-flex items-center gap-2">
                        <Mail className="w-6 h-6 text-[#1E3A8A]" /> Tutor Application Email Templates
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Multiple templates for the &quot;Become a Tutor&quot; confirmation email. Tick one to make it active — applied instantly, no code changes.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={refresh}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg"
                        data-testid="tutor-templates-refresh"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button
                        type="button"
                        onClick={onCreate}
                        data-testid="tutor-templates-create"
                        className="inline-flex items-center gap-2 bg-[#1E3A8A] text-white px-4 py-2 text-sm font-semibold rounded-lg hover:bg-blue-800"
                    >
                        <Plus className="w-4 h-4" /> New Template
                    </button>
                </div>
            </div>

            {flash && (
                <div data-testid="tutor-templates-flash" className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> {flash}
                </div>
            )}
            {error && (
                <div data-testid="tutor-templates-error" className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm inline-flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> {error}
                </div>
            )}

            {/* Active banner */}
            {activeTemplate && (
                <div className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-5" data-testid="tutor-templates-active-banner">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <BadgeCheck className="w-6 h-6 text-amber-300" />
                            <div>
                                <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">Currently Active · Sent to every new tutor applicant</div>
                                <div className="font-heading text-lg font-bold mt-0.5">{activeTemplate.name}</div>
                                <div className="text-xs text-blue-100 mt-0.5">Subject: {activeTemplate.subject}</div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => onPreview(activeTemplate)}
                            disabled={busy === `preview:${activeTemplate.id}`}
                            data-testid="tutor-templates-active-preview"
                            className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white text-sm font-semibold px-3 py-2 rounded-lg"
                        >
                            <Eye className="w-4 h-4" /> Preview
                        </button>
                    </div>
                </div>
            )}

            {/* Templates list */}
            <div className="grid lg:grid-cols-2 gap-4" data-testid="tutor-templates-list">
                {items.length === 0 ? (
                    <div className="lg:col-span-2 bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500">
                        No templates yet. Click <strong>New Template</strong> to create one.
                    </div>
                ) : items.map((tpl) => (
                    <article
                        key={tpl.id}
                        data-testid={`tutor-template-card-${tpl.id}`}
                        className={`bg-white border-2 rounded-2xl p-5 transition-all ${
                            tpl.is_active
                                ? "border-emerald-400 shadow-lg shadow-emerald-100/60"
                                : "border-slate-200 hover:border-blue-300"
                        }`}
                    >
                        {/* Header row with radio */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <button
                                type="button"
                                onClick={() => onActivate(tpl)}
                                disabled={tpl.is_active || busy === `activate:${tpl.id}`}
                                data-testid={`tutor-template-radio-${tpl.id}`}
                                className="flex items-start gap-2 flex-1 min-w-0 text-left"
                                title={tpl.is_active ? "Currently active" : "Click to activate this template"}
                            >
                                <div className="mt-1 shrink-0">
                                    {busy === `activate:${tpl.id}`
                                        ? <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                                        : tpl.is_active
                                            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 fill-emerald-100" />
                                            : <Circle className="w-5 h-5 text-slate-300 hover:text-blue-500" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[#0F172A] truncate">{tpl.name}</div>
                                    <div className="text-xs text-slate-500 truncate mt-0.5">Subject: {tpl.subject}</div>
                                </div>
                            </button>
                            {tpl.is_active && (
                                <span data-testid={`tutor-template-active-badge-${tpl.id}`} className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shrink-0">
                                    <BadgeCheck className="w-3 h-3" /> Currently Active
                                </span>
                            )}
                        </div>

                        {/* Body excerpt */}
                        <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 max-h-24 overflow-hidden mb-3 relative">
                            <div dangerouslySetInnerHTML={{ __html: tpl.body_html.replace(/<\/?p[^>]*>/g, " ").slice(0, 240) + "…" }} />
                        </div>

                        {/* Meta */}
                        <div className="text-[11px] text-slate-400 mb-3">
                            Updated {(tpl.updated_at || "").slice(0, 10)} by {tpl.updated_by}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                type="button"
                                onClick={() => onPreview(tpl)}
                                disabled={busy === `preview:${tpl.id}`}
                                data-testid={`tutor-template-preview-${tpl.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 rounded-md"
                            >
                                {busy === `preview:${tpl.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview
                            </button>
                            <button
                                type="button"
                                onClick={() => onEdit(tpl)}
                                data-testid={`tutor-template-edit-${tpl.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-md"
                            >
                                <Edit3 className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                                type="button"
                                onClick={() => onDuplicate(tpl)}
                                disabled={busy === `dup:${tpl.id}`}
                                data-testid={`tutor-template-duplicate-${tpl.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-md"
                            >
                                {busy === `dup:${tpl.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Duplicate
                            </button>
                            <button
                                type="button"
                                onClick={() => onDelete(tpl)}
                                disabled={busy === `del:${tpl.id}` || items.length <= 1}
                                data-testid={`tutor-template-delete-${tpl.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-700 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
                                title={items.length <= 1 ? "Cannot delete the only template" : "Delete this template"}
                            >
                                {busy === `del:${tpl.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
                            </button>
                        </div>
                    </article>
                ))}
            </div>

            {/* Editor modal */}
            {editor && (
                <TemplateEditor
                    template={editor}
                    placeholders={placeholders}
                    sampleFields={sampleFields}
                    onClose={() => setEditor(null)}
                    onSaved={async () => {
                        setEditor(null);
                        await refresh();
                        showFlash("Template saved.");
                    }}
                />
            )}

            {/* Preview modal */}
            {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
        </div>
    );
}


/* =========================== Editor Modal ============================ */

function TemplateEditor({ template, placeholders, sampleFields, onClose, onSaved }) {
    const isNew = template.mode === "create";
    const [name, setName] = useState(template.name || "");
    const [subject, setSubject] = useState(template.subject || "");
    const [bodyHtml, setBodyHtml] = useState(template.body_html || "");
    const [activate, setActivate] = useState(template.activate || false);
    const [saving, setSaving] = useState(false);
    const [livePreview, setLivePreview] = useState(null);
    const [err, setErr] = useState("");
    const [livePreviewBusy, setLivePreviewBusy] = useState(false);

    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
    }, [onClose]);

    const validate = () => {
        if (!name.trim()) { setErr("Template name is required"); return false; }
        if (!subject.trim()) { setErr("Subject is required"); return false; }
        if (!bodyHtml.trim()) { setErr("Body cannot be empty"); return false; }
        setErr("");
        return true;
    };

    const save = async (alsoActivate) => {
        if (!validate()) return;
        setSaving(true);
        setErr("");
        try {
            const payload = {
                name: name.trim(),
                subject: subject.trim(),
                body_html: bodyHtml,
                activate: !!alsoActivate,
            };
            if (isNew) {
                await api.post("/admin/tutor-email-templates", payload);
            } else {
                await api.patch(`/admin/tutor-email-templates/${template.id}`, payload);
            }
            await onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const refreshLivePreview = async () => {
        setLivePreviewBusy(true);
        try {
            const { data } = await api.post("/admin/tutor-email-templates/preview", { subject, body_html: bodyHtml });
            setLivePreview(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Preview failed");
        } finally {
            setLivePreviewBusy(false);
        }
    };

    const insertPlaceholder = (ph) => {
        const tag = `{{${ph}}}`;
        setBodyHtml((b) => b + tag);
    };

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="tutor-template-editor"
        >
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
                {/* header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <div>
                        <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase">{isNew ? "Create" : "Edit"} Template</div>
                        <h3 className="font-heading text-xl font-bold text-[#0F172A]">{isNew ? "New tutor application template" : name || "Editing template"}</h3>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="tutor-template-editor-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto grid md:grid-cols-2 gap-0 divide-x divide-slate-200">
                    {/* Form */}
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Template name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Not Actively Hiring — Database Acknowledgement"
                                data-testid="tutor-template-name-input"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">Email subject</label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Thank you for applying — Anand Tutorials"
                                data-testid="tutor-template-subject-input"
                                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-semibold text-slate-600">Email body (HTML supported)</label>
                                <button type="button" onClick={refreshLivePreview} disabled={livePreviewBusy} className="text-xs text-blue-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid="tutor-template-live-preview-btn">
                                    {livePreviewBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Refresh live preview
                                </button>
                            </div>
                            <textarea
                                value={bodyHtml}
                                onChange={(e) => setBodyHtml(e.target.value)}
                                rows={12}
                                placeholder="<p>Hi {{first_name}}, …</p>"
                                data-testid="tutor-template-body-input"
                                className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">Use HTML for formatting. Placeholders are replaced with the applicant&apos;s actual data at send time.</p>
                        </div>

                        {/* Placeholder buttons */}
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Insert placeholder</label>
                            <div className="flex flex-wrap gap-1.5">
                                {placeholders.map((ph) => (
                                    <button
                                        key={ph}
                                        type="button"
                                        onClick={() => insertPlaceholder(ph)}
                                        data-testid={`tutor-template-placeholder-${ph}`}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-mono bg-blue-50 hover:bg-blue-100 text-blue-800 rounded"
                                    >
                                        {"{{"}{ph}{"}}"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {err && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded inline-flex items-center gap-1.5" data-testid="tutor-template-editor-error">
                                <AlertTriangle className="w-4 h-4" /> {err}
                            </div>
                        )}
                    </div>

                    {/* Live preview */}
                    <div className="p-6 bg-slate-50 overflow-y-auto">
                        <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                            <Eye className="w-3.5 h-3.5" /> Live preview · sample data
                        </div>
                        {livePreview ? (
                            <div data-testid="tutor-template-live-preview-output">
                                <div className="bg-white border border-slate-200 rounded-lg p-3 mb-2">
                                    <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Subject</div>
                                    <div className="text-sm font-semibold text-[#0F172A]">{livePreview.subject || "—"}</div>
                                </div>
                                <div className="bg-white border border-slate-200 rounded-lg p-2">
                                    <iframe
                                        title="Email preview"
                                        srcDoc={livePreview.html}
                                        className="w-full h-[420px] border-0 rounded"
                                    />
                                </div>
                                <div className="mt-3 text-[10px] text-slate-500">
                                    Sample applicant: <strong>{sampleFields.name}</strong> · {sampleFields.email}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white border-2 border-dashed border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500">
                                Click <strong>Refresh live preview</strong> to render the email with sample data.
                            </div>
                        )}
                    </div>
                </div>

                {/* footer */}
                <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={activate}
                            onChange={(e) => setActivate(e.target.checked)}
                            data-testid="tutor-template-activate-checkbox"
                            className="w-4 h-4 rounded text-emerald-600"
                        />
                        Set as currently active after save
                    </label>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            data-testid="tutor-template-cancel"
                            className="px-4 py-2 text-sm rounded-lg bg-slate-200 hover:bg-slate-300 disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        {!activate && (
                            <button
                                type="button"
                                onClick={() => save(false)}
                                disabled={saving}
                                data-testid="tutor-template-save"
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-60"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save (keep inactive)
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => save(true)}
                            disabled={saving}
                            data-testid="tutor-template-save-activate"
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />} Save &amp; Activate
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


/* =========================== Preview Modal ============================ */

function PreviewModal({ preview, onClose }) {
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handler);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[120] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="tutor-template-preview-modal"
        >
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                    <div>
                        <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase">Preview</div>
                        <h3 className="font-heading text-lg font-bold text-[#0F172A]">{preview.name}</h3>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-slate-100 text-slate-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="overflow-y-auto p-5 bg-slate-50">
                    <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3">
                        <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Subject</div>
                        <div className="text-sm font-semibold text-[#0F172A]">{preview.subject || "—"}</div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-2">
                        <iframe title="Email preview" srcDoc={preview.html} className="w-full h-[520px] border-0 rounded" />
                    </div>
                    <div className="mt-3 text-[11px] text-slate-500">
                        Sample applicant: <strong>{preview.sample_fields?.name}</strong> · {preview.sample_fields?.email}
                    </div>
                </div>
            </div>
        </div>
    );
}
